import type { MapKind, MindmapDoc, MindNode } from "@etus-mindmap/shared/api";
import { BRANCH_PALETTE } from "../components/NodeDetailsPanel";

// Templates prontos = um kind existente + um doc semeado (esqueleto rotulado).
// Não há kind/migration/worker novo: o doc semeado é idêntico ao que o editor
// produziria, e POST /api/maps já valida/semeia o doc (mesmo caminho do import).
// Cores vêm da paleta do editor (BRANCH_PALETTE) para a herança de cor por ramo
// bater com o que o usuário editaria depois.

export interface MapTemplate {
  id: string; // slug estável
  name: string; // rótulo no card
  description: string; // 1 linha
  kind: MapKind; // reusa layout existente
  title: string; // título default no create
  build: () => MindmapDoc; // nodes (esqueleto + color por nível) + edges: []
}

// Cores nomeadas (índices da BRANCH_PALETTE: green, red, amber, sky, violet, pink, slate).
const GREEN = BRANCH_PALETTE[0];
const RED = BRANCH_PALETTE[1];
const AMBER = BRANCH_PALETTE[2];
const SKY = BRANCH_PALETTE[3];
const VIOLET = BRANCH_PALETTE[4];
const PINK = BRANCH_PALETTE[5];
const SLATE = BRANCH_PALETTE[6];

// ---- Builder de árvore -----------------------------------------------------
// Cada spec é [label, color?, children?]; ids são gerados estáveis (t1, t2…) em
// pré-ordem. Mantém os build() abaixo declarativos e legíveis.
type Spec = [label: string, color?: string, children?: Spec[]];

function tree(root: Spec): MindmapDoc {
  const nodes: MindNode[] = [];
  let seq = 0;
  const walk = (spec: Spec, parentId: string | null): void => {
    const [label, color, children] = spec;
    const id = `t${++seq}`;
    const node: MindNode = { id, label, parentId };
    if (color) node.color = color;
    nodes.push(node);
    for (const child of children ?? []) walk(child, id);
  };
  walk(root, null);
  return { nodes, edges: [] };
}

export const MAP_TEMPLATES: MapTemplate[] = [
  {
    id: "opportunity-tree",
    name: "Árvore de oportunidades",
    description: "Resultado → oportunidade → solução → experimento (Teresa Torres).",
    kind: "org",
    title: "Árvore de oportunidades",
    build: () =>
      tree([
        "Resultado desejado (métrica)",
        GREEN,
        [
          [
            "Oportunidade (necessidade / dor)",
            SKY,
            [
              ["Solução", AMBER, [["Experimento (teste de suposição)", VIOLET]]],
              ["Solução", AMBER],
            ],
          ],
          [
            "Oportunidade (necessidade / dor)",
            SKY,
            [["Solução", AMBER, [["Experimento (teste de suposição)", VIOLET]]]],
          ],
        ],
      ]),
  },
  {
    id: "okr-planning",
    name: "OKR Planning",
    description: "Objetivo → key results → iniciativas (cascata trimestral).",
    kind: "org",
    title: "OKR Planning",
    build: () =>
      tree([
        "Objetivo (trimestre)",
        GREEN,
        [
          [
            "Key Result (métrica)",
            SKY,
            [
              ["Iniciativa", SLATE],
              ["Iniciativa", SLATE],
            ],
          ],
          ["Key Result (métrica)", SKY, [["Iniciativa", SLATE]]],
          ["Key Result (métrica)", SKY],
        ],
      ]),
  },
  {
    id: "marketing-campaign",
    name: "Plano de campanha de marketing",
    description: "Objetivos, público, mensagem, canais, cronograma e orçamento.",
    kind: "mindmap",
    title: "Plano de campanha de marketing",
    build: () =>
      tree([
        "Campanha",
        undefined,
        [
          ["Objetivos & KPIs", GREEN],
          ["Público-alvo (personas)", SKY],
          ["Mensagem & proposta", VIOLET],
          [
            "Canais",
            AMBER,
            [
              ["Orgânico", AMBER],
              ["Pago", AMBER],
            ],
          ],
          ["Cronograma & marcos", RED],
          ["Orçamento", SLATE],
        ],
      ]),
  },
  {
    id: "brainstorm",
    name: "Brainstorm",
    description: "Tema central com ramos de ideias soltas.",
    kind: "mindmap",
    title: "Brainstorm",
    build: () =>
      tree([
        "Tema central",
        undefined,
        [
          ["Ideia 1", SKY],
          ["Ideia 2", AMBER],
          ["Ideia 3", VIOLET],
          ["Ideia 4", GREEN],
        ],
      ]),
  },
  {
    id: "retrospective",
    name: "Retrospectiva",
    description: "Continuar, parar e começar — retro de time.",
    kind: "mindmap",
    title: "Retrospectiva",
    build: () =>
      tree([
        "Retrospectiva",
        undefined,
        [
          ["Continuar (o que funcionou)", GREEN],
          ["Parar (o que atrapalhou)", RED],
          ["Começar (o que tentar)", AMBER],
        ],
      ]),
  },
  {
    id: "project-plan",
    name: "Plano de projeto",
    description: "Fases do projeto com entregáveis (WBS).",
    kind: "org",
    title: "Plano de projeto",
    build: () =>
      tree([
        "Projeto",
        GREEN,
        [
          ["Fase 1 — Descoberta", SKY, [["Entregável", SLATE]]],
          ["Fase 2 — Execução", AMBER, [["Entregável", SLATE]]],
          ["Fase 3 — Entrega", VIOLET, [["Entregável", SLATE]]],
        ],
      ]),
  },
  // ---- Leva 2 -------------------------------------------------------------
  {
    id: "swot",
    name: "Análise SWOT",
    description: "Forças, fraquezas, oportunidades e ameaças numa grade 2×2.",
    kind: "matrix",
    title: "Análise SWOT",
    build: () =>
      // Raiz = título; os 4 filhos diretos são os quadrantes (ordem = posição na
      // grade 2×2); descendentes viram itens dentro de cada quadrante.
      tree([
        "Análise SWOT",
        undefined,
        [
          ["Forças", GREEN, [["Item", GREEN]]],
          ["Fraquezas", RED, [["Item", RED]]],
          ["Oportunidades", SKY, [["Item", SKY]]],
          ["Ameaças", AMBER, [["Item", AMBER]]],
        ],
      ]),
  },
  {
    id: "org-chart",
    name: "Organograma de time",
    description: "Liderança, áreas e times numa hierarquia.",
    kind: "org",
    title: "Organograma",
    build: () =>
      tree([
        "Liderança",
        GREEN,
        [
          ["Área", SKY, [["Time / pessoa", SLATE]]],
          ["Área", AMBER, [["Time / pessoa", SLATE]]],
          ["Área", VIOLET, [["Time / pessoa", SLATE]]],
        ],
      ]),
  },
  {
    id: "content-calendar",
    name: "Calendário de conteúdo",
    description: "Publicações organizadas por período numa linha do tempo.",
    kind: "timeline",
    title: "Calendário de conteúdo",
    build: () =>
      tree([
        "Calendário de conteúdo",
        undefined,
        [
          ["Semana 1", SKY, [["Publicação", SLATE]]],
          ["Semana 2", AMBER, [["Publicação", SLATE]]],
          ["Semana 3", VIOLET, [["Publicação", SLATE]]],
        ],
      ]),
  },
  {
    id: "stakeholder-map",
    name: "Mapa de stakeholders",
    description: "Partes interessadas agrupadas por relação com o projeto.",
    kind: "mindmap",
    title: "Mapa de stakeholders",
    build: () =>
      tree([
        "Stakeholders",
        undefined,
        [
          ["Patrocinadores", GREEN],
          ["Time interno", SKY],
          ["Usuários", AMBER],
          ["Parceiros / externos", PINK],
        ],
      ]),
  },
  {
    id: "five-whys",
    name: "5 Porquês",
    description: "Cadeia de causas até a raiz do problema.",
    kind: "org",
    title: "5 Porquês",
    build: () =>
      tree([
        "Problema",
        RED,
        [
          [
            "Por quê? (1)",
            AMBER,
            [
              [
                "Por quê? (2)",
                AMBER,
                [
                  [
                    "Por quê? (3)",
                    AMBER,
                    [["Por quê? (4)", AMBER, [["Por quê? (5)", AMBER, [["Causa raiz", GREEN]]]]]],
                  ],
                ],
              ],
            ],
          ],
        ],
      ]),
  },
  {
    id: "user-journey",
    name: "Jornada do usuário",
    description: "Etapas da experiência com ações, emoções e dores.",
    kind: "timeline",
    title: "Jornada do usuário",
    build: () =>
      tree([
        "Jornada do usuário",
        undefined,
        [
          ["Descoberta", SKY, [["Ação / emoção / dor", SLATE]]],
          ["Consideração", VIOLET, [["Ação / emoção / dor", SLATE]]],
          ["Decisão", AMBER, [["Ação / emoção / dor", SLATE]]],
          ["Uso", GREEN, [["Ação / emoção / dor", SLATE]]],
          ["Retenção", PINK, [["Ação / emoção / dor", SLATE]]],
        ],
      ]),
  },
];
